import React, { useCallback, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { AutocompleteInput } from "../../components/AutocompleteInput";
import { formatMoneyBR } from "../../utils/format";

type Option = { id: number; label: string };

export const TransfersPage: React.FC = () => {
  const { client } = useAuth();
  const [fromAccount, setFromAccount] = useState<Option | null>(null);
  const [toAccount, setToAccount] = useState<Option | null>(null);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [occurredAt, setOccurredAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchBankAccounts = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/bank-accounts?q=${encodeURIComponent(query)}&take=20&skip=0`
      );
      if (!response.ok) return [];
      const payload = (await response.json()) as {
        data: Array<{ id: number; bank?: { name: string } | null; agency: string; accountNumber: string }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: item.bank
          ? `${item.bank.name} — ag ${item.agency} / cc ${item.accountNumber}`
          : `ag ${item.agency} / cc ${item.accountNumber}`,
      }));
    },
    [client]
  );

  const submitTransfer = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!fromAccount || !toAccount) {
      setError("Selecione a conta de origem e a conta de destino.");
      return;
    }
    if (fromAccount.id === toAccount.id) {
      setError("As contas de origem e destino devem ser diferentes.");
      return;
    }
    const parsedAmount = Number(amount.replace(",", "."));
    if (!parsedAmount || parsedAmount <= 0) {
      setError("Informe um valor válido.");
      return;
    }

    setLoading(true);
    try {
      const response = await client.post("/mov/transferencias", {
        fromBankAccountId: fromAccount.id,
        toBankAccountId: toAccount.id,
        description,
        amount: parsedAmount,
        occurredAt,
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { error?: string };
        setError(payload.error ?? "Falha ao criar transferência.");
        return;
      }

      setSuccess(`Transferência de ${formatMoneyBR(parsedAmount)} criada com sucesso.`);
      setDescription("");
      setAmount("");
      setOccurredAt(new Date().toISOString().slice(0, 10));
      setFromAccount(null);
      setToAccount(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4" style={{ maxWidth: 720 }}>
      <h1 className="mb-4">Transferências bancárias</h1>

      <div className="card shadow-sm">
        <div className="card-header" style={{ borderTop: "3px solid #0d6efd", background: "#f8f9fa" }}>
          <span className="fw-semibold">Nova transferência entre contas</span>
        </div>
        <div className="card-body">
          <form onSubmit={submitTransfer}>
            <div className="row g-3">
              <div className="col-md-6">
                <AutocompleteInput
                  label="Conta origem"
                  placeholder="Buscar conta de origem"
                  value={fromAccount?.id ?? null}
                  selectedLabel={fromAccount?.label}
                  fetchOptions={fetchBankAccounts}
                  onSelect={setFromAccount}
                />
              </div>
              <div className="col-md-6">
                <AutocompleteInput
                  label="Conta destino"
                  placeholder="Buscar conta de destino"
                  value={toAccount?.id ?? null}
                  selectedLabel={toAccount?.label}
                  fetchOptions={fetchBankAccounts}
                  onSelect={setToAccount}
                />
              </div>

              {fromAccount && toAccount && fromAccount.id === toAccount.id && (
                <div className="col-12">
                  <div className="alert alert-warning py-2 mb-0 small">
                    As contas de origem e destino devem ser diferentes.
                  </div>
                </div>
              )}

              <div className="col-md-6">
                <label className="form-label fw-semibold">Descrição</label>
                <input
                  className="form-control"
                  value={description}
                  placeholder="Descrição da transferência"
                  onChange={(event) => setDescription(event.target.value)}
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Valor (R$)</label>
                <input
                  className="form-control"
                  value={amount}
                  placeholder="0,00"
                  onChange={(event) => setAmount(event.target.value)}
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Data</label>
                <input
                  type="date"
                  className="form-control"
                  value={occurredAt}
                  onChange={(event) => setOccurredAt(event.target.value)}
                  required
                />
              </div>

              {error && (
                <div className="col-12">
                  <div className="alert alert-danger py-2 mb-0">{error}</div>
                </div>
              )}
              {success && (
                <div className="col-12">
                  <div className="alert alert-success py-2 mb-0">{success}</div>
                </div>
              )}

              <div className="col-12 d-flex gap-2">
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={loading || !fromAccount || !toAccount}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Processando...
                    </>
                  ) : (
                    "Criar transferência"
                  )}
                </button>
                {success && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setSuccess(null)}
                  >
                    Nova transferência
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
